/**
 * React Query hooks encapsulating every API interaction. Components consume
 * these rather than calling {@link apiFetch} directly, so caching, polling and
 * invalidation live in one place.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { City, LogRequest, StoredDevice } from './types';

/** Query keys, centralised to keep invalidation consistent. */
export const queryKeys = {
  devices: ['devices'] as const,
  logRequests: ['log-requests'] as const,
  logs: ['logs'] as const,
};

/**
 * Lists cities and their devices.
 *
 * To minimise Firestore reads ("selekty"), this query loads **once** on first
 * mount and is then served from cache indefinitely: no interval polling, no
 * refetch on window focus/reconnect/remount. An explicit reload from Firebase
 * is done only via {@link useRefreshDevices} (the "Odśwież" button).
 */
export function useDevices() {
  return useQuery({
    queryKey: queryKeys.devices,
    queryFn: () => apiFetch<City[]>('/api/devices'),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Forces a fresh read of the device list from Firestore (backend
 * `?refresh=true`, bypassing the 60 s server cache) and writes the result into
 * the {@link useDevices} cache. This is the only path that deliberately incurs
 * a Firestore read on the device list, triggered by an explicit operator click.
 */
export function useRefreshDevices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<City[]>('/api/devices?refresh=true'),
    onSuccess: (cities) => {
      queryClient.setQueryData(queryKeys.devices, cities);
    },
  });
}

/**
 * Lists all log-fetch requests. Polls every 3 s so PENDING requests visibly
 * transition to COMPLETED/EXPIRED as devices respond.
 */
export function useLogRequests() {
  return useQuery({
    queryKey: queryKeys.logRequests,
    queryFn: () => apiFetch<LogRequest[]>('/api/log-requests'),
    refetchInterval: 3_000,
  });
}

/** Lists the stored-log tree (devices → requests → files). */
export function useLogsTree() {
  return useQuery({
    queryKey: queryKeys.logs,
    queryFn: () => apiFetch<StoredDevice[]>('/api/logs'),
  });
}

/**
 * Initiates a log-fetch request for a device. On success, refreshes the request
 * list so the new PENDING request appears immediately.
 */
export function useFetchLogs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceHash, filesCount }: { deviceHash: string; filesCount: number }) =>
      apiFetch<LogRequest>(`/api/devices/${encodeURIComponent(deviceHash)}/fetch-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filesCount }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.logRequests });
    },
  });
}
